import graphql__64e09c2d5615a8b53c40 from "./__generated__/useMapElementsFilteredIn_map.graphql.js";import { useCallback, useMemo } from "react";
import { graphql, useFragment } from "react-relay";
import { useSelector } from "react-redux";
import { isEqual, intersection, flatMap } from "lodash";

import { useMapContext } from "../MapContext";

import { initialActiveIdsFor } from "../../../reducers/map/filters";

// User filters narrow the list of users displayed on the map through their own refetch,
// and never hide map elements, so only the two filter types below are handled here.
const TAXONOMY_FILTER_TYPENAME = "MapFilterTaxonomy";
const ELEMENT_FILTER_TYPENAME = "MapFilterElement";

const isElementFilter = ({ typename }) => typename === ELEMENT_FILTER_TYPENAME;

// The ids a filter is able to select. A taxonomy filter also displays the children of
// the taxonomies it's configured with, so those belong to its scope as well.
const selectableIdsFor = filter => {
  if (filter.__typename === ELEMENT_FILTER_TYPENAME) {
    return filter.mapElements.map(({ id }) => id);
  }

  return flatMap(filter.mapTaxonomies, ({ id, children }) => [
    id,
    ...children.map(child => child.id)
  ]);
};

const elementFiltersFrom = filterLists => {
  const filters = flatMap(filterLists, ({ filters: listFilters }) => listFilters);

  return filters
    .filter(({ __typename }) =>
      [TAXONOMY_FILTER_TYPENAME, ELEMENT_FILTER_TYPENAME].includes(__typename)
    )
    .map(filter => ({
      id: filter.id,
      typename: filter.__typename,
      filterElementsOutOnInitialize: filter.filterElementsOutOnInitialize,
      selectableIds: selectableIdsFor(filter)
    }));
};

// The ids of the element that a filter compares its own selection against : an element
// filter selects the elements themselves, while a taxonomy filter selects the taxonomies
// the elements are associated to.
const filterableIdsFor = (filter, element) => {
  if (isElementFilter(filter)) return [element.id];

  return element.mapTaxonomies.map(({ id }) => id);
};

const activeIdsFor = (filter, selectionsByFilterId) => {
  const selection = selectionsByFilterId[filter.id];

  // The filter hasn't registered its initial selection yet, so we derive it the same way
  // it does, to display the elements it's about to show as soon as the map is rendered.
  if (!selection) {
    return initialActiveIdsFor(
      filter.filterElementsOutOnInitialize,
      filter.selectableIds
    );
  }

  if (isElementFilter(filter)) return selection.activeElementIds;

  return selection.activeTaxonomyIds;
};

const coversElement = (filter, element) =>
  intersection(filter.selectableIds, filterableIdsFor(filter, element)).length > 0;

const matchesElement = (filter, element, selectionsByFilterId) =>
  intersection(
    activeIdsFor(filter, selectionsByFilterId),
    filterableIdsFor(filter, element)
  ).length > 0;

export const useMapElementsFilteredIn = ({ map: inputMap, elements: inputElements }) => {
  const map = useFragment(useMapElementsFilteredIn_map, inputMap) || {};

  const elements = useMemo(
    () => map.elements.edges.map(({ node }) => node),
    [map.elements.edges]
  );

  const { mapKey } = useMapContext();
  const selectionsByFilterId = useSelector(
    ({ map }) => map[mapKey].filters.byFilterId,
    isEqual
  );

  // Each filter is reduced to the set of ids it's able to select, so that the options
  // it's configured with only ever apply to the elements it can actually reach, and
  // never to the elements covered by the other filters of the map.
  const filters = useMemo(() => elementFiltersFrom(map.filterLists), [map.filterLists]);

  const isFilteredIn = useCallback(
    element => {
      const coveringFilters = filters.filter(filter => coversElement(filter, element));

      // A filter always displays the items it's currently showing on the map, so an
      // element is displayed as soon as one of the filters that can select it has it
      // selected. When no filter is able to select the element, none of them has a say
      // on whether it should be displayed, and it's always shown.
      if (!coveringFilters.length) return true;

      return coveringFilters.some(filter =>
        matchesElement(filter, element, selectionsByFilterId)
      );
    },
    [filters, selectionsByFilterId]
  );

  return useMemo(() => {
    return inputElements.map(inputElement => {
      const element = elements.find(({ id }) => id === inputElement.id);

      return { ...inputElement, isFilteredIn: isFilteredIn(element) };
    });
  }, [elements, inputElements, isFilteredIn]);
};

const useMapElementsFilteredIn_map = graphql__64e09c2d5615a8b53c40;
